// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll as hideAllTippyInstances, Instance as TippyInstance } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import FancyScroll from "@tps/App/Components/Common/FancyScroll";
import LazyTippy from "@tps/App/Components/Common/LazyTippy";
import setCloseAutoComplete from "@tps/App/Handlers/XNG/searchAutoComplete";
import setShowDropShadow from "@tps/App/Handlers/XNG/showDropShadow";
import { color, ellipsis, styledScrollbar } from "@tps/XDL/Utilities";
import { FontWeightProperty } from "csstype";
import isMobile from "ismobilejs";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    /**
     * XNG-Changes:
     * onClick callback method
     */
    onClick?: () => void;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** XNG-Changes: tippy trigger prop */
        tippyTrigger?: string;
        /** XNG-Changes: To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
        /** To show Menu from begging, x=0 */
        isProductsMenu?: boolean;
    }
>;

// Xng-Changes
const mapDispatchToProps = {
    setShowDropShadow,
    setCloseAutoComplete,
};

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    MenuPresentationProps &
    ThemeProps<BaseTheme> &
    ResolveThunks<typeof mapDispatchToProps>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        menuHeight?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<MenuStyleProps>`
    list-style: none;
    background: ${color("common.white")};
    width: ${getProp("width", 300)}px;
    max-width: calc((100vw / 3) - 16px);
    max-height: calc(100vh - 233px);
    min-height: 150px;
    padding: 6px 0;
    list-style: none;
    margin: 0;
    overflow: auto;
    box-shadow: 0 -1px 0 0 ${color("common.border")};
    &[data-depth]:not([data-depth="1"]) {
        border-left: 1px solid ${color("common.border")};
    }

    ${styledScrollbar(4)}
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen?: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    .trigger-item,
    .arrow {
        transition: transform ease-in-out 250ms;
        color: ${color("common.ui4")};
        &,
        &:hover ${TypographyStyle} {
            text-decoration: none;
        }
    }

    &[aria-expanded="true"] {
        .arrow {
            transform: rotate(180deg);
            color: ${color("common.ui8")};
        }
        .trigger.trigger-item ${TypographyStyle} {
            color: ${color("common.ui8")};
        }
    }

    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean; weight?: FontWeightProperty }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 22px;
    ${ellipsis(2)}
`;

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "primary.main",
    },
    active: {
        background: "common.ui9",
        text: "common.white",
        icon: "common.white",
    },
};

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemStyle = styled.div<MenuItemStyleProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;

    + [data-tippy-root] {
        > div,
        ${MenuStyle} {
            height: 100%;
        }
    }
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 300)}px;
        top: 0;
    }

    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 300)}px;
        top: 0;
    }

    ${StyledButton} {
        display: inline-block;
        width: calc(100% - 20px);
        padding: 6px 12px;
        line-height: 22px;
        font-size: 14px;
    }

    ${IconWrapper} {
        top: 7px;
        height: 20px;
        width: 20px;
        color: ${color(menuItemColorStyle.default.icon)};
    }

    ${TypographyStyle} {
        font-size: 14px;
        line-height: 22px;
    }

    &:hover,
    &[aria-expanded="true"] {
        background: ${color(menuItemColorStyle.active.background)};
        ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }

    &[aria-expanded="false"]:hover {
        background: ${color(menuItemColorStyle.active.background, 0.33)};
    }

    ${injectCss}
`;

/**
 * XNG-Changes
 */
const hideOnPopperBlurPlugin = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: TippyInstance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    if (
                        event.relatedTarget &&
                        event.relatedTarget instanceof HTMLElement &&
                        !instance.popper.contains(event.relatedTarget)
                    ) {
                        instance.hide();
                    }
                });
            },
        };
    },
};

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 300,
        tippyTrigger: "mouseenter focusin",
        hideOnClick: false,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // This make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }

        this.menuWrapperRef.current.blur();

        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }

        menuTrigger.blur();
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, isProductsMenu, mergeCss, ...otherProps } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        // XNG-Changes
                        const parentMenu = React.createRef<HTMLUListElement>();

                        return (
                            <FancyScroll refElement={parentMenu}>
                                <MenuStyle
                                    width={isProductsMenu ? otherProps.width : 300} // XNG-Changes
                                    ref={parentMenu}
                                    className="MenuStyle"
                                    data-depth={currentDepth}
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                >
                                    {childMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, index) => {
                                            /**
                                             * XNG-Changes
                                             */
                                            const maxDepth = item.maxDepth || parentDepth;

                                            const hasChildren =
                                                item.children?.some(o => !o.excludeFromNavigation) &&
                                                currentDepth < maxDepth;

                                            if (
                                                (item.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink) ||
                                                !this.state.shouldRender
                                            ) {
                                                return null;
                                            }

                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={otherProps.width}
                                                    css={applyStyledProp(
                                                        ["cssOverrides", "menuItem"],
                                                        resolvedMergeCss,
                                                    )}
                                                    key={`${item.title}_${index}`}
                                                    className="MenuItem"
                                                    data-test-selector="menuItem"
                                                    data-depth={currentDepth}
                                                    onClick={() => {
                                                        this.closeMenuWithoutChildren(hasChildren);
                                                    }}
                                                >
                                                    <Clickable
                                                        href={item.url}
                                                        onClick={() => {
                                                            item.onClick?.();
                                                            this.closeMenu();
                                                            hideAllTippyInstances();
                                                        }}
                                                        aria-label={item.title}
                                                        {...applyProp("menuItemClickableProps")}
                                                    >
                                                        <MenuItemText
                                                            hasChildren={hasChildren}
                                                            {...applyProp("menuItemTypographyProps")}
                                                            weight="normal"
                                                            data-test-selector="menuItemText"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                        {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                    </Clickable>
                                                </MenuItemStyle>
                                            );
                                            return (
                                                <StyledWrapper key={`${item.title}_${index}`} as="li">
                                                    {!hasChildren && menuItem}
                                                    {hasChildren && (
                                                        <LazyTippy
                                                            render={() =>
                                                                renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                )
                                                            }
                                                            trigger="mouseenter"
                                                            placement="right-start"
                                                            interactive={true}
                                                            animation={false}
                                                            offset={[0, -1]}
                                                            delay={[120, 250]}
                                                            touch={item.url ? "hold" : true}
                                                            plugins={[hideOnPopperBlurPlugin]}
                                                            getReferenceClientRect={() => {
                                                                if (
                                                                    !parentMenu.current ||
                                                                    !parentMenu ||
                                                                    typeof parentMenu.current[
                                                                        "getBoundingClientRect"
                                                                    ] === "undefined"
                                                                ) {
                                                                    return new DOMRect(0, 0, 0, 0);
                                                                }
                                                                return parentMenu.current!.getBoundingClientRect();
                                                            }}
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "preventOverflow",
                                                                        options: { mainAxis: false },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            {menuItem}
                                                        </LazyTippy>
                                                    )}
                                                </StyledWrapper>
                                            );
                                        })}
                                </MenuStyle>
                            </FancyScroll>
                        );
                    };

                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <LazyTippy
                                render={() => <>{renderMenuItems(otherProps.maxDepth || 0, menuItems, 1)}</>}
                                trigger={this.props.tippyTrigger}
                                placement="bottom-start"
                                hideOnClick={this.props.hideOnClick}
                                interactive={true}
                                animation={false}
                                delay={[120, 250]}
                                offset={[-12, 21.5]}
                                plugins={[hideOnPopperBlurPlugin]}
                                touch={["hold", 250]}
                                appendTo="parent"
                                onShow={instance => {
                                    hideAllTippyInstances();
                                    this.props.setShowDropShadow({ showDropShadow: true });
                                    const inputElement = document.getElementsByClassName("header-search-input")?.[1];
                                    (inputElement as any)?.blur();
                                }}
                                onHidden={instance => {
                                    this.props.setShowDropShadow({ showDropShadow: false });
                                }}
                                popperOptions={{
                                    modifiers: [
                                        {
                                            name: "arrow",
                                            options: {
                                                padding: 0,
                                                element: ".menu-arrow",
                                            },
                                        },
                                        {
                                            name: "computeStyles",
                                            options: {
                                                // roundOffsets: ({ x, y }: any) => ({
                                                //     x: isProductsMenu
                                                //         ? document
                                                //               .getElementById("header-logo")!
                                                //               .getBoundingClientRect().left
                                                //         : x,
                                                //     y,
                                                // }),
                                            },
                                        },
                                    ],
                                }}
                                // Xng-Changes: TPS-652
                                onTrigger={() => this.props.setCloseAutoComplete({ closeAutoComplete: true })}
                            >
                                <MenuWrapper
                                    css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                    {...{ "aria-labelledby": descriptionId }}
                                >
                                    {React.cloneElement(menuTrigger, {
                                        id: descriptionId.split(" ").join("_"),
                                        className: `trigger trigger-item`,
                                    })}
                                </MenuWrapper>
                            </LazyTippy>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default connect(null, mapDispatchToProps)(withTheme(Menu));

export { MenuItemText };
